/**
 * Merge a freshly-fetched page of `notifications` into `existingNotifications`
 * in place. `offset === 0` (first page / refresh) replaces the list wholesale;
 * any other offset (subsequent page) upserts each incoming notification by
 * `id` — updating it in place if already present, appending it otherwise —
 * so paginated loads don't duplicate or drop entries already in the list.
 */
const mergeNotifications = (existingNotifications, notifications, offset = 0) => {
  if (!Array.isArray(notifications)) return;

  if (offset === 0) {
    existingNotifications.splice(0, existingNotifications.length, ...notifications);
    return;
  }

  notifications.forEach(notification => {
    const existingIndex = existingNotifications.findIndex(existing => existing.id === notification.id);
    if (existingIndex === -1) {
      existingNotifications.push(notification);
    } else {
      existingNotifications.splice(existingIndex, 1, {
        ...existingNotifications[existingIndex],
        ...notification,
      });
    }
  });
};

/**
 * Vuex mutations for the "alerts" (notifications) module.
 *
 * Synchronous state writers for the unread/read notification lists, their
 * counts, and load/error flags. Async work (API calls) lives in
 * operations.js, which commits these mutations with the results.
 */
export default {
  setNotifications(state, payload) {
    state.notifications = payload;
  },
  /** Push `notification` onto `state[type]` (e.g. `unreadNotifications` or `readNotifications`). */
  setAddNotification(state, { notification, type }) {
    state[type].push(notification);
  },
  /** Prepend `newNotifications` onto the aggregate `state.notifications` list, skipping any already present by `id`. */
  setUpdateNotifications(state, newNotifications) {

    if (!newNotifications) return;

    const uniqueNotifications = newNotifications.filter((newNotification) => {
      return !state.notifications.some(
        (existingNotification) => existingNotification.id === newNotification.id
      );
    });

    state.notifications = [...uniqueNotifications, ...state.notifications];
  },
  setUnreadNotificationsCount(state, payload) {
    state.unreadNotificationsCount = payload;
  },
  incrementReadNotificationsCount(state) {
    state.readNotificationsCount += 1;
  },
  /** Set the unread count and merge `payload.results` into `state.unreadNotifications` at `payload.offset` (see `mergeNotifications`). */
  setUnreadNotifications(state, payload) {
    state.unreadNotificationsCount = payload.count;
    mergeNotifications(state.unreadNotifications, payload.results, payload.offset);
  },
  /** Set the read count and merge `payload.results` into `state.readNotifications` at `payload.offset` (see `mergeNotifications`). */
  setReadNotifications(state, payload) {
    state.readNotificationsCount = payload.count;
    mergeNotifications(state.readNotifications, payload.results, payload.offset);
  },
  setIsNotificationLoaded(state, payload) {
    state.isLoaded = payload;
  },
  setNotificationsCount(state, payload) {
    state.count = payload;
  },
  setNotificationsError(state, payload) {
    state.notificationsError = payload;
  },
  setUnreadCountError(state, payload) {
    state.unreadCountError = payload;
  },
  // Loader mutations are intentionally disabled while notifications refresh in the background.
  // setNotificationsLoading(state, payload) {
  //   state.notificationsLoading = payload;
  // },
  // setUnreadCountLoading(state, payload) {
  //   state.unreadCountLoading = payload;
  // },
  /** Remove the notification matching `notification.id` from `state[type]` (e.g. `unreadNotifications` or `readNotifications`), if present. */
  setRemoveNotification(state, { notification, type }) {
    const index = state[type].findIndex(n => n.id === notification.id);
    if (index !== -1) {
        state[type].splice(index, 1);
    }
  },

  /**
   * Move every notification out of `unreadNotifications` into
   * `readNotifications` (marking each `read: true`, deduped by `id`), zero
   * out the unread count, and mark every entry in the aggregate
   * `state.notifications` list as read too.
   */
  MARK_ALL_NOTIFICATIONS_READ(state) {
    // Move all unread notifications to read notifications
    const unreadToMove = [...state.unreadNotifications];
    
    // Add to read notifications (avoiding duplicates)
    unreadToMove.forEach(notification => {
      notification.read = true;
      const existsInRead = state.readNotifications.some(n => n.id === notification.id);
      if (!existsInRead) {
        state.readNotifications.unshift(notification); // Add to beginning to maintain order
      }
    });
    
    // Clear unread notifications
    state.unreadNotifications = [];
    
    // Update counts
    state.unreadNotificationsCount = 0;
    state.readNotificationsCount = state.readNotifications.length;
    
    // Also update the main notifications array if it exists
    if (state.notifications && state.notifications.length > 0) {
      state.notifications = state.notifications.map(notification => ({
        ...notification,
        read: true
      }));
    }
  },

  // setIsRead(state, notificationId) {
  //   const notification = state.notifications.find(
  //     (n) => n.id === notificationId
  //   );
  //   if (notification) {      
  //     notification.read = true;
  //     console.log(notification);
  //   }
  // },

  /** Reset all notification lists, counts, and error state back to empty/initial values. */
  setClearNotifications(state){
    state.notifications = [];
    state.unreadNotifications = [];
    state.readNotifications = [];
    state.unreadNotificationsCount = 0;
    state.readNotificationsCount = 0;
    state.notificationsError = null;
    state.unreadCountError = null;
  }
};
